import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { justForSideEffectWithPayload, justForSideEffect } from 'src/infrastructure/saga.helpers'
import {
  SetProperty,
  GetDivergencePayload,
  SetDivergence,
  SetDivergenceFilters,
  SetActiveDivergence,
  SetDivergenceFilterOptions,
  DivergenceDemandsState,
  ValidateManagerBadgePayload,
  ProductNotFoundPayload,
  StartItemResolutionPayload,
  ValidateProductPayload,
  PaginationInfo,
} from './DivergenceDemandsTypes'
import { cloneDeep } from 'lodash'

export const initialState: DivergenceDemandsState = {
  user: {
    badge: undefined,
    user: undefined,
    userId: undefined,
  },
  divergence: [],
  divergenceFilters: {},
  divergenceFilterOptions: undefined,
  activeDivergenceId: undefined,
  divergenceActiveAddress: undefined,
  divergenceActiveProduct: undefined,
  divergenceActiveProductDesc: undefined,
  activeDivergenceItemId: undefined,
  productResolutionData: undefined,
  pagination: undefined,
}

const validateManagerBadge = justForSideEffectWithPayload<ValidateManagerBadgePayload>()
const getDivergence = justForSideEffectWithPayload<GetDivergencePayload>()
const getDivergenceFilterOptions = justForSideEffect()
const startDivergenceResolution = justForSideEffectWithPayload<{ divergenceId: string, prodBarcode: string }>()
const validateProduct = justForSideEffectWithPayload<ValidateProductPayload>()
const productNotFound = justForSideEffectWithPayload<ProductNotFoundPayload>()
const startItemResolution = justForSideEffectWithPayload<StartItemResolutionPayload>()
const setProductResolutionData = (
  state: DivergenceDemandsState,
  action: PayloadAction<any>,
): DivergenceDemandsState => ({
  ...state,
  productResolutionData: action.payload,
})

const clearProductResolutionData = (
  state: DivergenceDemandsState,
): DivergenceDemandsState => ({
  ...state,
  productResolutionData: undefined,
})

const setDivergence = (
  state: DivergenceDemandsState,
  action: PayloadAction<SetDivergence>,
): DivergenceDemandsState => ({
  ...state,
  divergence: action.payload.divergence,
})

const setPagination = (
  state: DivergenceDemandsState,
  action: PayloadAction<PaginationInfo>,
): DivergenceDemandsState => ({
  ...state,
  pagination: action.payload,
})

const startDivergencePolling = justForSideEffect()

const stopDivergencePolling = justForSideEffect()

const setDivergenceFilters = (
  state: DivergenceDemandsState,
  action: PayloadAction<SetDivergenceFilters>,
): DivergenceDemandsState => ({
  ...state,
  divergenceFilters: action.payload.filters,
})

const setActiveDivergence = (
  state: DivergenceDemandsState,
  action: PayloadAction<SetActiveDivergence>,
): DivergenceDemandsState => ({
  ...state,
  divergenceActiveProduct: action.payload.product,
  divergenceActiveProductDesc: action.payload.productDesc,
  divergenceActiveAddress: action.payload.location,
  activeDivergenceId: action.payload.divergenceId,
})

const setDivergenceFilterOptions = (
  state: DivergenceDemandsState,
  action: PayloadAction<SetDivergenceFilterOptions>,
): DivergenceDemandsState => ({
  ...state,
  divergenceFilterOptions: action.payload.filterOptions,
})

const setProperty = (state: DivergenceDemandsState, action: PayloadAction<SetProperty>): DivergenceDemandsState => {
  const { property, value } = action.payload

  return {
    ...state,
    [property]: value,
  }
}

const clearState = (): DivergenceDemandsState => initialState

const clearProperty = (
  state: DivergenceDemandsState,
  action: PayloadAction<string | string[]>,
): DivergenceDemandsState => {
  const newState = cloneDeep(state)

  if (Array.isArray(action.payload)) {
    action.payload.forEach((property) => {
      newState[property] = initialState[property]
    })
  } else {
    newState[action.payload] = initialState[action.payload]
  }

  return newState
}

const slice = createSlice({
  name: 'divergenceDemands',
  initialState,
  reducers: {
    startDivergenceResolution,
    validateManagerBadge,
    validateProduct,
    productNotFound,
    startItemResolution,
    setProperty,
    clearState,
    clearProperty,
    getDivergence,
    getDivergenceFilterOptions,
    setDivergence,
    setPagination,
    setDivergenceFilters,
    setDivergenceFilterOptions,
    setActiveDivergence,
    setProductResolutionData,
    clearProductResolutionData,
    startDivergencePolling,
    stopDivergencePolling,
  },
})

export default slice.reducer

export const { actions } = slice
